<template>
  <div class="category-page">
    <!-- Loading Shimmer State -->
    <div v-if="isLoading" class="shimmer-category">
      <div class="shimmer-category-header">
        <div class="shimmer-block rounded" style="height: 32px; width: 250px;"><div class="shimmer-overlay"></div></div>
        <div class="shimmer-block" style="height: 14px; width: 80px; margin-top: 12px;"><div class="shimmer-overlay"></div></div>
      </div>
      <div class="shimmer-video-grid">
        <div v-for="n in 9" :key="'shimmer-cat-' + n" class="shimmer-video-card">
          <div class="shimmer-thumb"><div class="shimmer-overlay"></div></div>
          <div class="shimmer-card-info">
            <div class="shimmer-card-avatar"><div class="shimmer-overlay"></div></div>
            <div class="shimmer-card-text">
              <div class="shimmer-card-title"><div class="shimmer-overlay"></div></div>
              <div class="shimmer-card-title short"><div class="shimmer-overlay"></div></div>
              <div class="shimmer-card-meta"><div class="shimmer-overlay"></div></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="status-container error">
      <vue-feather type="alert-circle" size="64" stroke-width="1"></vue-feather>
      <h2>Error Loading Category</h2>
      <p>{{ error }}</p>
      <p class="error-detail">Category ID: {{ $route.params.id }}</p>
      <div class="error-actions">
        <button class="btn-home btn-retry" @click="fetchCategories">Try Again</button>
        <router-link to="/" class="btn-home">Go Home</router-link>
      </div>
    </div>

    <!-- Category Not Found -->
    <div v-else-if="!category" class="status-container">
      <vue-feather type="folder" size="64" stroke-width="1"></vue-feather>
      <h2>Category Not Found</h2>
      <p>The category you're looking for doesn't exist or has been removed.</p>
      <p class="error-detail">Category ID: {{ $route.params.id }}</p>
      <router-link to="/" class="btn-home">Go Home</router-link>
    </div>

    <!-- Category Content -->
    <div class="category-videos" v-else>
      <div class="category-header">
        <h1 class="category-title">{{ currentLanguage === 'EN' ? (category.nameEN || category.nameTA) : category.nameTA }}</h1>
        <p class="video-count" v-if="filteredVideos.length > 0">
            {{ filteredVideos.length }} videos<template v-if="draftCount"> · {{ draftCount }} unpublished (admin view)</template>
        </p>
      </div>

      <!-- Videos Grid -->
      <div v-if="filteredVideos.length > 0">
        <video-grid cols="3" :videos="filteredVideos" :userImages="userImages"></video-grid>

        <!-- Infinite scroll loading indicator -->
        <div v-if="isLoadingMore" class="loading-more">
          <span class="loading-spinner"></span>
          <span>Loading more videos…</span>
        </div>
      </div>

      <!-- No Videos -->
      <div v-else class="no-videos">
        <vue-feather type="video-off" size="48" stroke-width="1"></vue-feather>
        <h3>No Videos Yet</h3>
        <p>There are no videos in this category yet.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { createSessionPagination } from '../utils/sessionPagination';
import { API } from 'aws-amplify';
import { getCategoryModel } from '../graphql/custom.js';
import { captureError } from '../utils/analytics';
import VideoGrid from '../components/VideoGrid.vue'
import VueFeather from 'vue-feather';

export default {
  name: 'category-videos',
  components: {
      VideoGrid,
      VueFeather
  },
  inject: {
      languageRef: { from: 'currentLanguage', default: 'EN' },
      isAdminRef: { from: 'isAdminRef', default: null }
  },
  data() {
      return {
          category: null,
          pageProgress: null,
          isLoading: true,
          isLoadingMore: false,
          userImages: [],
          error: null,
          primaryNextToken: null,
          secondaryNextToken: null,
          pageSize: 100,
      }
  },
  computed: {
      currentLanguage() {
          return this.languageRef || 'EN';
      },
      filteredVideos() {
          const primaryVideos = this.category?.primaryVideos?.items || [];
          const secondaryVideos = this.category?.secondaryVideos?.items || [];

          // Combine both primary and secondary videos
          const allVideos = [...primaryVideos, ...secondaryVideos];

          // Remove duplicates (in case a video has both primary and secondary set to same category)
          const uniqueVideos = allVideos.filter((video, index, self) =>
              index === self.findIndex(v => v.id === video.id)
          );

          // Playable videos only; admins also see unpublished ones (drafts etc.)
          // so they can open and update them. VideoThumb labels those cards.
          return uniqueVideos.filter(video =>
              (this.isAdminViewer || !video.publishState || video.publishState === 'published') &&
              (!video.status || ['ready', 'processingReady'].includes(video.status))
          );
      },
      isAdminViewer() {
          // unwrapInjectedRef is on (main.js), so the injected ref arrives unwrapped.
          return this.isAdminRef === true;
      },
      draftCount() {
          return this.filteredVideos.filter(video =>
              video.publishState && video.publishState !== 'published'
          ).length;
      },
      isSubcategory() {
          return !!this.category?.categoryModelChildrenId;
      },
      hasMore() {
          return !!(this.primaryNextToken || this.secondaryNextToken);
      }
  },
  created() {
      this.fetchCategories();
  },
  mounted() {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
  },
  beforeUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
  },
  watch: {
      '$route.params.id': {
          handler() {
              this.fetchCategories();
          }
      },
  },
  methods: {
      async fetchCategories() {
          const progress = createSessionPagination('category', this.$route.params.id);
          this.pageProgress = progress;
          this.isLoadingMore = false;
          this.isLoading = true;
          this.category = null;
          this.error = null;
          this.primaryNextToken = null;
          this.secondaryNextToken = null;

          try {
              const categoryId = this.$route.params.id;
              console.log('Loading category:', categoryId);

              // Restore saved depth, then follow each relation's token on scroll.
              const categoryRes = await API.graphql({
                  query: getCategoryModel,
                  variables: { id: categoryId, limit: this.pageSize }
              });

              if (this.pageProgress !== progress) return;
              const category = categoryRes.data.getCategoryModel;
              if (category) {
                  this.primaryNextToken = category.primaryVideos?.nextToken || null;
                  this.secondaryNextToken = category.secondaryVideos?.nextToken || null;
              }

              console.log('Category loaded:', category);
              this.category = category;
              await progress.restore(() => this.pageProgress === progress && this.loadMore());

              // Update page title
              if (this.category) {
                  const categoryName = this.category.nameEN || this.category.nameTA || 'Category';
                  document.title = `${categoryName} - Eelam TV`;
              } else {
                  document.title = 'Category Not Found - Eelam TV';
              }
          } catch (err) {
              if (this.pageProgress !== progress) return;
              console.error('Error loading category:', err);
              captureError(err, { source: 'api_fetch', component: 'CategoryVideos', operation: 'fetchCategories', categoryId: this.$route.params.id });
              this.category = null;
              // Extract error message
              if (err.errors && err.errors.length > 0) {
                  this.error = err.errors[0].message;
              } else if (err.message) {
                  this.error = err.message;
              } else {
                  this.error = 'Failed to load category';
              }
          } finally {
              if (this.pageProgress === progress) {
                  this.isLoading = false;
                  this.$nextTick(() => this.handleScroll());
              }
          }
      },

      async loadMore() {
          if (this.isLoadingMore || !this.hasMore || !this.category) return;

          const progress = this.pageProgress;
          this.isLoadingMore = true;
          const categoryId = this.category.id;
          const primaryToken = this.primaryNextToken;
          const secondaryToken = this.secondaryNextToken;

          try {
              const pageRes = await API.graphql({
                  query: getCategoryModel,
                  variables: {
                      id: categoryId,
                      limit: this.pageSize,
                      primaryNextToken: primaryToken || null,
                      secondaryNextToken: secondaryToken || null,
                  }
              });

              const page = pageRes.data.getCategoryModel;
              if (!page || this.pageProgress !== progress) return false;

              // Only merge/advance the relations that still had a token, so a
              // null token doesn't re-append page 1 of the other relation.
              if (primaryToken) {
                  if (page.primaryVideos?.items?.length) {
                      this.category.primaryVideos.items.push(...page.primaryVideos.items);
                  }
                  this.primaryNextToken = page.primaryVideos?.nextToken || null;
              }
              if (secondaryToken) {
                  if (page.secondaryVideos?.items?.length) {
                      this.category.secondaryVideos.items.push(...page.secondaryVideos.items);
                  }
                  this.secondaryNextToken = page.secondaryVideos?.nextToken || null;
              }
              progress.loaded();
              this.$nextTick(() => this.handleScroll());
              return true;
          } catch (err) {
              console.error('Error loading more category videos:', err);
          } finally {
              if (this.pageProgress === progress) {
                  this.isLoadingMore = false;
              }
          }
      },

      handleScroll() {
          if (!this.hasMore || this.isLoadingMore || this.isLoading) return;

          const threshold = 600;
          const scrolledToBottom =
              window.innerHeight + window.scrollY >= document.body.offsetHeight - threshold;

          if (scrolledToBottom) {
              this.loadMore();
          }
      },
  }
}
</script>

<style scoped>
.category-page {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    min-height: calc(100vh - 150px);
}

.status-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 60px;
    text-align: center;
    color: var(--text-tertiary);
}

.status-container.error {
    color: #f44336;
}

.status-container h2 {
    margin: 20px 0 10px;
    color: var(--text-secondary);
}

.status-container p {
    margin-bottom: 10px;
    color: var(--text-tertiary);
}

.error-detail {
    font-size: 12px;
    font-family: monospace;
    background: var(--bg-tertiary);
    padding: 8px 16px;
    border-radius: 4px;
    margin-bottom: 20px !important;
}

.shimmer-category {
    padding: 0;
}

.shimmer-category-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-color);
}

.shimmer-video-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 360px));
    gap: 20px;
    padding: 0;
}

@media (max-width: 1200px) {
    .shimmer-video-grid {
        grid-template-columns: repeat(2, minmax(0, 360px));
    }
}

@media (max-width: 768px) {
    .shimmer-video-grid {
        grid-template-columns: minmax(0, 400px);
        justify-content: center;
    }
}

.loading-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px 20px;
    color: var(--text-tertiary);
    font-size: 14px;
}

.loading-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid var(--border-color);
    border-top-color: var(--accent-primary);
    border-radius: 50%;
    animation: category-spin 0.8s linear infinite;
}

@keyframes category-spin {
    to { transform: rotate(360deg); }
}

.no-videos {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    color: var(--text-tertiary);
}

.no-videos h3 {
    margin: 20px 0 10px;
    color: var(--text-secondary);
}

.no-videos p {
    margin-bottom: 20px;
    color: var(--text-tertiary);
}

.btn-home {
    display: inline-block;
    padding: 12px 24px;
    background: var(--accent-primary);
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 500;
    transition: all 0.2s;
    margin-top: 10px;
}

.btn-home:hover {
    background: #e00e0f;
    transform: translateY(-2px);
}

.error-actions {
    display: flex;
    gap: 12px;
}

.btn-retry {
    border: none;
    font-size: inherit;
    font-family: inherit;
    cursor: pointer;
}

.category-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-color);
}

.category-title {
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 8px 0;
}

.video-count {
    font-size: 14px;
    color: var(--text-tertiary);
    margin: 0;
}

@media (max-width: 768px) {
    .category-page {
        padding: 16px;
    }

    .category-title {
        font-size: 22px;
    }

    .status-container {
        padding-top: 40px;
    }
}
</style>
